import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import { expenseStore } from "../expenses";
import { invoiceStore } from "../invoices";
import { categoryStore } from "../categories";
import { taxPolicyStore } from "../taxPolicies";
import { receiptStore } from "../receipts";
import { buildExpensesCsv, buildExportFilename } from "./csv";
import {
  generateExpensesPdf,
  filterExpensesForExport,
} from "./pdf";
import { buildAuditPackage } from "./zipBundle";
import type {
  ExportFormat,
  ExportRecord,
  ExportRequest,
  ExportStore,
  ExportDataSources,
} from "./types";

interface ExportRow {
  id: string;
  user_id: string;
  filename: string;
  format: ExportFormat;
  row_count: number;
  filter_json: Record<string, unknown> | null;
  created_at: string;
  size_bytes: number | null;
  mime_type?: string | null;
  storage_path?: string | null;
}

const ROW_COLUMNS =
  "id, user_id, filename, format, row_count, filter_json, created_at, size_bytes, mime_type, storage_path";
const BUCKET = "exports";

function rowToRecord(row: ExportRow): ExportRecord {
  return {
    id: row.id,
    filename: row.filename,
    format: row.format,
    rowCount: Number(row.row_count),
    filterJson: row.filter_json ?? {},
    createdAt: row.created_at,
    sizeBytes: row.size_bytes ?? undefined,
    mimeType: row.mime_type ?? undefined,
    storagePath: row.storage_path ?? undefined,
    // Supabase records store metadata only — content is regenerated on
    // download from live data (CSV) or re-downloaded from the bucket (PDF).
  };
}

function triggerDownloadFromBlob(blob: Blob, filename: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function triggerBrowserDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  triggerDownloadFromBlob(blob, filename);
}

function collectSources(): ExportDataSources {
  return {
    expenses: expenseStore.list(),
    invoices: invoiceStore.list(),
    categories: categoryStore.list().map((c) => ({ id: c.id, label: c.label })),
    taxPolicies: taxPolicyStore
      .list()
      .map((p) => ({ id: p.id, category: p.category })),
    receipts: receiptStore.list().map((r) => ({
      id: r.id,
      linkedExpenseId: r.linkedExpenseId,
      linkedInvoiceId: r.linkedInvoiceId,
    })),
  };
}

export function createSupabaseExportStore(): ExportStore {
  const listeners = new Set<() => void>();

  let snapshot: ExportRecord[] = [];
  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function commit(next: ExportRecord[]): void {
    snapshot = next;
    emit();
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      commit([]);
      return;
    }

    const orgId = getActiveOrganizationId();
    if (!orgId) {
      commit([]);
      return;
    }

    const { data: rows, error } = await supabase
      .from("exports")
      .select(ROW_COLUMNS)
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[exports] list failed: ${error.message}`);
      commit([]);
      return;
    }

    const typedRows = (rows ?? []) as ExportRow[];
    commit(typedRows.map(rowToRecord));
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) {
      loadPromise = loadFromServer().finally(() => {
        initialized = true;
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        initialized = false;
        commit([]);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        initialized = false;
        void ensureLoaded();
      }
    });
    subscribeActiveOrg(() => {
      initialized = false;
      void ensureLoaded();
    });
  }

  return {
    list(): ExportRecord[] {
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      return snapshot;
    },

    async create(req: ExportRequest): Promise<ExportRecord> {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("You must be signed in to create an export.");
      }
      const userId = userData.user.id;

      const orgId = getActiveOrganizationId();
      if (!orgId) {
        throw new Error(
          "No active organization selected. Reload the page and try again.",
        );
      }

      const sources = collectSources();
      const createdAt = new Date();
      const id =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `x_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

      if (req.format === "zip") {
        // Audit package: CSV + PDF + raw receipts. Bundled client-side and
        // uploaded to the exports bucket alongside metadata. Uses the
        // bucket-missing fallback path same as PDF.
        const fullReceipts = receiptStore.list();
        const result = await buildAuditPackage(sources, req, fullReceipts);
        const blob = new Blob([result.zipBytes as BlobPart], {
          type: "application/zip",
        });
        const filename = buildExportFilename("zip", createdAt);
        const storagePath = `${userId}/${id}.zip`;

        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(storagePath, blob, {
            contentType: "application/zip",
            upsert: false,
          });

        const bucketMissing =
          uploadError != null &&
          (uploadError.message.toLowerCase().includes("bucket not found") ||
            uploadError.message.toLowerCase().includes("no such bucket") ||
            uploadError.message.toLowerCase().includes("bucketnotfound") ||
            uploadError.message.toLowerCase().includes("mime type"));

        if (uploadError && !bucketMissing) {
          throw new Error(`Failed to upload export: ${uploadError.message}`);
        }

        const resolvedStoragePath = bucketMissing ? null : storagePath;

        const { data: inserted, error: insertError } = await supabase
          .from("exports")
          .insert({
            id,
            organization_id: orgId,
            user_id: userId,
            filename,
            format: "zip",
            row_count: result.rowCount,
            filter_json: {
              ...req,
              _bundledReceipts: result.receiptCount,
              _missingReceipts: result.missingReceipts,
            },
            size_bytes: blob.size,
            mime_type: "application/zip",
            ...(resolvedStoragePath
              ? { storage_path: resolvedStoragePath }
              : {}),
          })
          .select(ROW_COLUMNS)
          .single();

        if (insertError || !inserted) {
          if (!bucketMissing) {
            await supabase.storage.from(BUCKET).remove([storagePath]);
          }
          throw new Error(
            `Failed to record export: ${insertError?.message ?? "unknown"}`,
          );
        }

        const record = rowToRecord(inserted as ExportRow);
        triggerDownloadFromBlob(blob, filename);
        initialized = true;
        commit([record, ...snapshot]);
        return record;
      }

      if (req.format === "pdf") {
        const filteredExpenses = filterExpensesForExport(sources.expenses, {
          dateFrom: req.dateFrom,
          dateTo: req.dateTo,
          categoryIds: req.categoryIds,
          userId: req.userId ?? null,
        });
        const blob = await generateExpensesPdf({
          expenses: filteredExpenses,
          categories: sources.categories,
          taxPolicies: sources.taxPolicies,
          orgName: "Proofly",
          dateRange: { from: req.dateFrom, to: req.dateTo },
        });
        const filename = buildExportFilename("pdf", createdAt);
        const storagePath = `${userId}/${id}.pdf`;
        const sizeBytes = blob.size;

        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(storagePath, blob, {
            contentType: "application/pdf",
            upsert: false,
          });

        // Bucket may not exist yet if migration 0015 hasn't run. Fall back to
        // metadata-only record + immediate local download so the user still
        // gets their PDF even before the bucket is provisioned.
        const bucketMissing =
          uploadError != null &&
          (uploadError.message.toLowerCase().includes("bucket not found") ||
            uploadError.message.toLowerCase().includes("no such bucket") ||
            uploadError.message.toLowerCase().includes("bucketnotfound"));

        if (uploadError && !bucketMissing) {
          throw new Error(`Failed to upload export: ${uploadError.message}`);
        }

        const resolvedStoragePath = bucketMissing ? null : storagePath;

        const { data: inserted, error: insertError } = await supabase
          .from("exports")
          .insert({
            id,
            organization_id: orgId,
            user_id: userId,
            filename,
            format: "pdf",
            row_count: filteredExpenses.length,
            filter_json: { ...req },
            size_bytes: sizeBytes,
            mime_type: "application/pdf",
            ...(resolvedStoragePath ? { storage_path: resolvedStoragePath } : {}),
          })
          .select(ROW_COLUMNS)
          .single();

        if (insertError || !inserted) {
          if (!bucketMissing) {
            await supabase.storage.from(BUCKET).remove([storagePath]);
          }
          throw new Error(
            `Failed to record export: ${insertError?.message ?? "unknown"}`,
          );
        }

        const record = rowToRecord(inserted as ExportRow);
        triggerDownloadFromBlob(blob, filename);
        initialized = true;
        commit([record, ...snapshot]);
        return record;
      }

      // CSV path. Preserve the original behaviour (metadata-only insert,
      // client-side download) so existing tests + UX aren't perturbed.
      const { content, rowCount } = buildExpensesCsv(sources, req);
      const filename = buildExportFilename("csv", createdAt);
      const sizeBytes = new Blob([content]).size;

      const { data: inserted, error: insertError } = await supabase
        .from("exports")
        .insert({
          id,
          organization_id: orgId,
          user_id: userId,
          filename,
          format: "csv",
          row_count: rowCount,
          filter_json: { ...req },
          size_bytes: sizeBytes,
          mime_type: "text/csv",
        })
        .select(ROW_COLUMNS)
        .single();

      if (insertError || !inserted) {
        throw new Error(
          `Failed to record export: ${insertError?.message ?? "unknown"}`,
        );
      }

      const record = rowToRecord(inserted as ExportRow);
      triggerBrowserDownload(content, filename);
      initialized = true;
      commit([record, ...snapshot]);
      return record;
    },

    async remove(id: string): Promise<void> {
      const target = snapshot.find((r) => r.id === id);
      const next = snapshot.filter((r) => r.id !== id);
      if (next.length !== snapshot.length) commit(next);
      if (target?.storagePath && target.format === "pdf") {
        void supabase.storage
          .from(BUCKET)
          .remove([target.storagePath])
          .then(({ error }) => {
            if (error) {
              // eslint-disable-next-line no-console
              console.warn(`[exports] storage delete failed: ${error.message}`);
            }
          });
      }
      void supabase
        .from("exports")
        .delete()
        .eq("id", id)
        .then(({ error }) => {
          if (error) {
            // eslint-disable-next-line no-console
            console.warn(`[exports] delete failed: ${error.message}`);
          }
        });
    },

    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// Exported so the Exports page can re-generate from live data when the user
// clicks the history row's Download button on a Supabase-backed record
// (which has no persisted content).
export function redownloadSupabaseRecord(record: ExportRecord): void {
  if (
    (record.format === "pdf" || record.format === "zip") &&
    record.storagePath
  ) {
    // Binary formats: fetch the bucket object directly. ZIP audit packages
    // can't be losslessly regenerated (receipt URLs may have rotated, rows
    // may have moved), so re-download is the only correct path.
    void supabase.storage
      .from(BUCKET)
      .download(record.storagePath)
      .then(({ data, error }) => {
        if (error || !data) return;
        triggerDownloadFromBlob(data, record.filename);
      });
    return;
  }
  const sources = collectSources();
  const req: ExportRequest = {
    format: record.format,
    dateFrom: (record.filterJson.dateFrom as string | undefined) ?? null,
    dateTo: (record.filterJson.dateTo as string | undefined) ?? null,
    categoryIds: record.filterJson.categoryIds as string[] | undefined,
    includeReceipts: Boolean(record.filterJson.includeReceipts),
    includeNotes: Boolean(record.filterJson.includeNotes),
    userId: (record.filterJson.userId as string | undefined) ?? null,
  };
  const { content } = buildExpensesCsv(sources, req);
  triggerBrowserDownload(content, record.filename);
}
